import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, DollarSign, Package, Calendar } from 'lucide-react';

interface RevenueData {
  total_revenue: number;
  today_revenue: number;
  month_revenue: number;
  total_delivered_orders: number;
  today_delivered_orders: number;
  month_delivered_orders: number;
}

const RevenueCard = () => {
  const { data: revenueData, isLoading } = useQuery({
    queryKey: ['revenue-analytics'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('revenue_analytics')
        .select('*')
        .single();

      if (error) throw error;
      return data as RevenueData;
    },
    refetchInterval: 30000, // Refetch every 30 seconds for real-time updates
  });

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-6">
              <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
              <div className="h-8 bg-muted rounded w-1/2"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (!revenueData) {
    return (
      <Card>
        <CardContent className="p-6 text-center text-muted-foreground">
          No revenue data available
        </CardContent>
      </Card>
    );
  }

  const stats = [
    {
      title: 'Total Revenue',
      value: `₹${Number(revenueData.total_revenue).toLocaleString('en-IN')}`,
      icon: DollarSign,
      description: `${revenueData.total_delivered_orders} orders delivered`,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
    },
    {
      title: "Today's Revenue",
      value: `₹${Number(revenueData.today_revenue).toLocaleString('en-IN')}`,
      icon: TrendingUp,
      description: `${revenueData.today_delivered_orders} orders today`,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
    },
    {
      title: 'This Month',
      value: `₹${Number(revenueData.month_revenue).toLocaleString('en-IN')}`,
      icon: Calendar,
      description: `${revenueData.month_delivered_orders} orders this month`,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
    },
    {
      title: 'Delivered Orders',
      value: revenueData.total_delivered_orders.toLocaleString('en-IN'),
      icon: Package,
      description: 'Total completed orders',
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {stats.map((stat, index) => (
        <Card key={index} className="relative overflow-hidden">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {stat.title}
            </CardTitle>
            <div className={`p-2 rounded-lg ${stat.bgColor}`}>
              <stat.icon className={`h-4 w-4 ${stat.color}`} />
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-1">
              <div className="text-2xl font-bold">{stat.value}</div>
              <p className="text-xs text-muted-foreground">{stat.description}</p>
            </div>
            {index === 1 && revenueData.today_revenue > 0 && (
              <Badge variant="secondary" className="mt-2 text-xs">
                Active Today
              </Badge>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default RevenueCard;